import { Component, Input, OnInit } from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { Router } from "@angular/router";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { Observable, OperatorFunction, lastValueFrom } from "rxjs";
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  switchMap,
  map,
} from "rxjs/operators";
import { AddressCounty } from "../../../../core/models/client/addclient/countyList";
import { AddressCountryRegion } from "../../../../core/models/client/addclient/countryRegionList";
import { VerifyClientList } from "../../../../core/models/client/verify/verifyClientList";
import { GetBCForSalesOrderDto } from "../../../../core/models/orders/getBCForSalesOrderDto";
import { LoaderService } from "../../../../core/services/loader.service";
import { ValidateLogin } from "../../../../core/models/validate-login.dto";
import { UserManagementService } from "../../../../core/services/user-management.service";
import { VerifyProspectRequest } from "../../../../core/models/prospect/verifyprospect/verifyProspectRequest";
import { SamplepoListingService } from "../../../../core/services/sample-po.service";
import { CommonModule } from "@angular/common";
import { TableModule } from "primeng/table";
import { HidedynamicsdatePipe } from "../../../../shared/pipes/hidedate/hidedynamicsdate.pipe";
import { ParentCompanyNameDto } from "../../../../core/models/client/parentCompanyNameDto";
import { SelectModule } from "primeng/select";
import { ProfileFormService } from "../../../../core/services/profile-form.service";
import { VerifyClientService } from "../../../../core/services/verify-client.service";
import { CardModule } from "primeng/card";
import { InputTextModule } from "primeng/inputtext";
import { TextareaModule } from "primeng/textarea";
import { ButtonModule } from "primeng/button";
import { AddNewClientService } from "../../../../core/services/add-new-client.service";
import { GenericResponseDto } from "../../../../core/models/generic-response.dto";
import { GetUserDto } from "../../../../core/models/users/users/getUserDto";

interface BrandingConsultant {
  name: string;
  id: number;
}

@UntilDestroy()
@Component({
  selector: "jni-verify-client",
  templateUrl: "./verify-client.component.html",
  imports: [
    CommonModule,
    TableModule,
    ReactiveFormsModule,
    SelectModule,
    HidedynamicsdatePipe,
    CardModule,
    InputTextModule,
    InputTextModule,
    TextareaModule,
    ButtonModule,
  ],
  styleUrls: ["./verify-client.component.scss"],
})
export class VerifyClientComponent implements OnInit {
  verifyClientForm: FormGroup;
  countryList: AddressCounty[] = [];
  verifyClientList: VerifyClientList[] = [];
  countryRegionList: AddressCountryRegion[] = [];
  verifyClientDto!: VerifyProspectRequest;
  getClientName: ParentCompanyNameDto[] = [];
  getFilteredClientName: ParentCompanyNameDto[] = [];
  getBrandingConsultantList: BrandingConsultant[] = [];
  showSmallLoader = false;
  prospectIdForConvert!: string | null | undefined;
  brandingConsultant = "";
  bcList: GetBCForSalesOrderDto[] = [];
  bacList: GetBCForSalesOrderDto[] = [];
  bcUserDefaultSelectionId = "";
  userId = 0;
  userPersonnelNumber!: string;

  @Input() verifyClientDtoFromQuickScreen?: VerifyProspectRequest;

  showCreateClientBtn = false;
  descriptionCharacterCond = false;

  constructor(
    private _verifyClientService: VerifyClientService,
    private _formBuilder: FormBuilder,
    private router: Router,
    private _addNewClientService: AddNewClientService,
    private _loaderService: LoaderService,
    private _loginService: UserManagementService,
    private _samplepoListingService: SamplepoListingService,
    private _profileFormService: ProfileFormService
  ) {
    this.verifyClientForm = this._formBuilder.group({
      id: 0,
      brandingConsultant: [""],
      country: ["USA"],
      clientName: [
        "",
        [
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(160),
        ],
      ],
      streetAddress: [""],
      zip: [""],
      companyUrl: ["", [Validators.minLength(2), Validators.maxLength(500)]],
      dUNS: ["", [Validators.minLength(2), Validators.maxLength(10)]],
    });

    if (this.router.getCurrentNavigation()?.extras.state != undefined) {
      const state = this.router.getCurrentNavigation()?.extras.state;
      this.prospectIdForConvert = typeof state === "string" ? state : null;
    }
  }

  formErrors: Record<string, string> = {
    clientName: "",
    companyUrl: "",
    streetAddress: "",
  };

  validationMessages: Record<string, Record<string, string>> = {
    clientName: {
      required: "Client name is required.",
      minlength: "Client name must be greater than 2 characters.",
      maxlength: "Client name must be less than 160 characters.",
    },
    companyUrl: {
      minlength: "Url must be greater than 2 characters.",
      maxlength: "Url must be less than 500 characters.",
    },
    dUNS: {
      minlength: "Duns# must be greater than 2 characters.",
      maxlength: "Duns# must be less than 10 characters.",
    },
    streetAddress: {
      required: "Client street address is required.",
    },
  };

  ngOnInit(): void {
    if (
      this.verifyClientDtoFromQuickScreen &&
      (this.verifyClientDtoFromQuickScreen.clientName ||
        this.verifyClientDtoFromQuickScreen.companyUrl ||
        this.verifyClientDtoFromQuickScreen.streetAddress)
    ) {
      this.verifyClientForm.patchValue({
        clientName:
          this.verifyClientDtoFromQuickScreen?.clientName?.trim() || "",
        streetAddress:
          this.verifyClientDtoFromQuickScreen?.streetAddress?.trim() || "",
        companyUrl:
          this.verifyClientDtoFromQuickScreen?.companyUrl?.trim() || "",
      });
    }

    this.getCountryRegionListData();
    this.getBrandingConsultantForAutoCompleteData();
    this.getUserProfile();
    this.getWhoISupport();
  }

  getWhoISupport() {
    this._samplepoListingService
      .getAllWhoISupportIfAdminGetAllBc()
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (res: GenericResponseDto<GetBCForSalesOrderDto[]>) => {
          if (res.data != null) {
            this.bcList = res.data;
            this.patchValueInBCField();
          }
          this.showSmallLoader = false;
        },
        error: () => {
          this.showSmallLoader = false;
        },
      });
  }

  patchValueInBCField() {
    // if (this.userPersonnelNumber && this.bcList.length) {
    //   const filterBacObject: GetBCForSalesOrderDto[] = this.bcList?.filter(
    //     (x) => x.personnelNumber === this.userPersonnelNumber
    //   );
    //   if (filterBacObject.length > 0) {
    //     this.bcUserDefaultSelectionId = filterBacObject[0].projectName;
    //   }
    // }

    // Task 11252 IsPrimary user will populate to BC dropdown list by default
    if (this.bcList?.length > 0) {
      const filterBacObject: GetBCForSalesOrderDto[] = this.bcList?.filter(
        x => x.isPrimary === true
      );
      if (filterBacObject.length > 0) {
        this.bcUserDefaultSelectionId = filterBacObject[0].projectName;
      }
    }
  }

  getUserProfile() {
    this._loginService
      .validateLogin()
      .pipe()
      .subscribe((res: GenericResponseDto<ValidateLogin>) => {
        this.userId = res.data.userId;
        this.getUserById(res?.data?.userId);
      });
  }

  getUserById(userId: number) {
    this._profileFormService.getUsersById(userId).subscribe({
      next: async (res: GenericResponseDto<GetUserDto>) => {
        if (res.data != null) {
          this.userPersonnelNumber = res.data.personnelNumber;
          this.patchValueInBCField();
        }
      },
    });
  }

  // getBCListData() {
  //   this._addNewClientService
  //     .getBCList()
  //     .pipe(untilDestroyed(this))
  //     .subscribe({
  //       next: (res) => {
  //         if (res.data) {
  //           this.bcList = res.data;
  //         }
  //       }
  //     });
  // }

  // getBACList(id: number) {
  //   this._addNewClientService
  //     .getBACList(id)
  //     .pipe(untilDestroyed(this))
  //     .subscribe({
  //       next: (res) => {
  //         this.bacList = res.data;
  //       }
  //     });
  // }

  logValidationErrors(group: FormGroup = this.verifyClientForm): void {
    Object.keys(group.controls).forEach((key: string) => {
      const abstractControl = group.get(key);
      if (abstractControl instanceof FormGroup) {
        this.logValidationErrors(abstractControl);
      } else {
        this.formErrors[key] = "";
        if (
          abstractControl &&
          !abstractControl.valid &&
          (abstractControl.touched || abstractControl.dirty)
        ) {
          const messages = this.validationMessages[key];
          for (const errorKey in abstractControl.errors) {
            if (errorKey && messages) {
              this.formErrors[key] += messages[errorKey] + " ";
            }
          }
        }
      }
    });
  }

  searchName: OperatorFunction<string, readonly string[]> = (
    text$: Observable<string>
  ) =>
    text$.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      switchMap(async (term: string) =>
        term.length < 3
          ? []
          : (await this.getClientNameForAutoCompleteData(term)).map(
              (client: ParentCompanyNameDto) => client.nameAliasD365
            )
      )
    );

  async getClientNameForAutoCompleteData(name: string) {
    this.getFilteredClientName = [];
    this.showSmallLoader = true;
    const res = await lastValueFrom(
      this._addNewClientService
        .getClientNameForAutoComplete(name)
        .pipe(untilDestroyed(this))
    );
    this.getClientName = res.data.map(x => {
      const client: ParentCompanyNameDto = {
        id: 0,
        nameAliasD365: x,
        organizationNameD365: "",
        accountNumber: "",
      };
      return client;
    });
    this.showSmallLoader = false;
    return this.getClientName;
  }

  // formatter = (state: any) => state.name;
  searchBrandingConsultant: OperatorFunction<
    string,
    readonly BrandingConsultant[]
  > = (text$: Observable<string>) =>
    text$.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      filter(term => term.length >= 3),
      map(term =>
        this.getBrandingConsultantList.filter(state =>
          new RegExp(term, "mi").test(state.name)
        )
      )
    );

  getBrandingConsultantForAutoCompleteData() {
    this._addNewClientService
      .GetAllConfiguredUser()
      .pipe(untilDestroyed(this))
      .subscribe({
        next: res => {
          this.getBrandingConsultantList = res?.data;
        },
      });
  }

  getCountryRegionListData() {
    this._addNewClientService
      .getCountryRegionList()
      .pipe(untilDestroyed(this))
      .subscribe({
        next: res => {
          this.countryRegionList = res?.data;
        },
      });
  }

  onClientVerify() {
    this.submitButtonlogValidationErrors();
    this._loaderService.setLoaderState(true);
    if (!this.verifyClientForm.valid) {
      this._loaderService.setLoaderState(false);
      return;
    }

    this.verifyClientDto =
      this.verifyClientForm.getRawValue() as VerifyProspectRequest;

    if (
      this.verifyClientDto?.clientName &&
      typeof this.verifyClientDto.clientName === "object" &&
      "name" in this.verifyClientDto.clientName
    ) {
      this.verifyClientDto.clientName = (
        this.verifyClientDto.clientName as { name: string }
      ).name;
    }

    if (
      this.verifyClientDto?.brandingConsultant &&
      typeof this.verifyClientDto.brandingConsultant === "object" &&
      "name" in this.verifyClientDto.brandingConsultant
    ) {
      this.verifyClientDto.brandingConsultant = (
        this.verifyClientDto.brandingConsultant as { name: string }
      ).name;
    }

    if (this.verifyClientDto?.country) {
      this.verifyClientDto.country = "";
    }

    if (this.verifyClientDto?.brandingConsultant === null) {
      this.verifyClientDto.brandingConsultant = "";
    }

    if (this.verifyClientDto) {
      this.verifyClientDto.clientName = this.verifyClientDto.clientName.trim();
      this.verifyClientDto.streetAddress =
        this.verifyClientDto.streetAddress.trim();
      this.verifyClientDto.dUNS = this.verifyClientDto.dUNS.trim();
      this.verifyClientDto.companyUrl = this.verifyClientDto.companyUrl.trim();
    }

    this._verifyClientService
      .verifyClientData(this.verifyClientDto)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (res: GenericResponseDto<VerifyClientList[]>) => {
          this.verifyClientList = res.data;
          this._loaderService.setLoaderState(false);
          this.showCreateClientBtn = true;
        },
        error: () => {
          this._loaderService.setLoaderState(false);
          this.showCreateClientBtn = true;
        },
      });
  }

  submitButtonlogValidationErrors(
    group: FormGroup = this.verifyClientForm
  ): void {
    Object.keys(group.controls).forEach((key: string) => {
      const abstractControl = group.get(key);
      if (abstractControl instanceof FormGroup) {
        this.submitButtonlogValidationErrors(abstractControl);
      } else {
        this.formErrors[key] = "";
        if (abstractControl && !abstractControl.valid) {
          const messages = this.validationMessages[key];
          for (const errorKey in abstractControl.errors) {
            if (errorKey && messages) {
              this.formErrors[key] += messages[errorKey] + " ";
            }
          }
        }
      }
    });
  }

  redirectToCreateClient() {
    if (
      this.prospectIdForConvert != undefined &&
      this.prospectIdForConvert != ""
    ) {
      this.router.navigateByUrl("/clients/active/add-new-client", {
        state: { prospectId: this.prospectIdForConvert },
      });
    } else {
      this.router.navigateByUrl("/clients/active/add-new-client", {
        state: this.verifyClientDto,
      });
    }
  }

  descriptionCharacterCount(obj: string) {
    if (obj) {
      if (obj.length > 45) this.descriptionCharacterCond = true;
    }
  }

  trimValue(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    inputElement.value = inputElement.value.trim();
  }

  verifyClient(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    inputElement.value = inputElement.value.trim();
    this.onClientVerify();
  }
}
